<script setup>
import { useInstanceStore } from '@/store/backendInstances'
import { computed, onMounted, ref, watch } from 'vue'

const props = defineProps({
  disabled: { type: Boolean, default: false },
  loadAuthenticationMethods: { type: Boolean, default: true },
  skipPermissionSimulation: { type: Boolean, default: false },
})
const modelValue = defineModel({ type: String })
const instanceStore = useInstanceStore()

const loading = ref(false)
const accounts = ref([])
const emailPasswordAuthRecords = ref([])
const usernamePasswordAuthRecords = ref([])
const remoteAuthRecords = ref([])

function findEmail(accountId) {
  if (!accountId) return null
  if (!emailPasswordAuthRecords.value) return null
  const emailPasswordAuthRecord = emailPasswordAuthRecords.value.find(record => accountId === record.accountId)
  if (emailPasswordAuthRecord) {
    return emailPasswordAuthRecord.email
  }
  return null
}

function findUsername(accountId) {
  if (!accountId) return null
  if (!usernamePasswordAuthRecords.value) return null
  const usernamePasswordAuthRecord = usernamePasswordAuthRecords.value.find(record => accountId === record.accountId)
  if (usernamePasswordAuthRecord) {
    return usernamePasswordAuthRecord.username
  }
  return null
}

function findRemoteAuthRecord(accountId) {
  if (!accountId) return [null, null]
  if (!remoteAuthRecords.value) return [null, null]
  const remoteAuthRecord = remoteAuthRecords.value.find(record => accountId === record.accountId)
  if (remoteAuthRecord) {
    return [remoteAuthRecord.remoteAuthenticationId, remoteAuthRecord.remoteAccountId]
  }
  return [null, null]
}

async function reloadAccounts() {
  loading.value = true
  emailPasswordAuthRecords.value = []
  accounts.value = []
  usernamePasswordAuthRecords.value = []
  remoteAuthRecords.value = []
  if (props.loadAuthenticationMethods) {
    emailPasswordAuthRecords.value = await instanceStore.queryEmailPasswordAuthRecords(
      { projection: { only: ['accountId', 'email'] } },
      undefined,
      props.skipPermissionSimulation
    )
    usernamePasswordAuthRecords.value = await instanceStore.queryUsernamePasswordAuthRecords(
      { projection: { only: ['username', 'accountId'] } },
      undefined,
      props.skipPermissionSimulation
    )
    remoteAuthRecords.value = await instanceStore.queryRemoteAuthRecords(
      { projection: { only: ['accountId', 'remoteAuthenticationId', 'remoteAccountId'] } },
      undefined,
      props.skipPermissionSimulation
    )
  }
  accounts.value = await instanceStore.getAccounts(undefined, props.skipPermissionSimulation)
  loading.value = false
}

const itemList = computed(() => {
  return accounts.value.map(account => {
    const accountId = account.accountId
    const email = findEmail(accountId)
    const username = findUsername(accountId)
    const [remoteAuthenticationId, remoteAccountId] = findRemoteAuthRecord(accountId)

    let accountString = `${account.displayName} (AccountId: ${account.accountId}`
    if (email) accountString += `, Email: ${email}`
    if (username) accountString += `, Username: ${username}`
    if (remoteAccountId) accountString += `, Remote Account Id: ${remoteAccountId}`
    if (remoteAuthenticationId) accountString += `, Remote Authentication Id: ${remoteAuthenticationId}`
    accountString = accountString + ')'
    return { key: accountId, title: accountString }
  })
})

watch(
  modelValue,
  async newValue => {
    if (newValue === null) return
    if (accounts.value.map(account => account.accountId).includes(newValue)) return
    await reloadAccounts()
  },
  {}
)

onMounted(async () => {
  await reloadAccounts()
})

function maybeEmit(update) {
  const accountId = update
  emit('picked-email', findEmail(accountId))
  emit('picked-username', findUsername(accountId))
  const [remoteAuthId, remoteAccountId] = findRemoteAuthRecord(accountId)
  emit('picked-remote-account-id', remoteAccountId)
  emit('picked-remote-auth-id', remoteAuthId)
}

const emit = defineEmits(['picked-remote-account-id', 'picked-remote-auth-id', 'picked-username', 'picked-email'])
</script>

<template>
  <v-autocomplete
    v-model="modelValue"
    variant="outlined"
    :loading="loading"
    :disabled="disabled"
    style="min-width: 250px"
    :label="$t('general_interface.account_picker.label')"
    :items="itemList"
    item-value="key"
    item-title="title"
    @update:model-value="maybeEmit"
  />
</template>

<style scoped></style>
